import { useState } from 'react'
import { Activity, Headphones, HeadphoneOff, Mic, MicOff, Monitor, MonitorOff, PhoneOff, Video, VideoOff } from 'lucide-react'
import { displayName, useSocialStore } from '@/store/social'
import { useVoiceStore } from '@/store/voice'
import { plural } from '@/lib/guildUi'
import { avatarUrl } from '@/lib/avatar'
import { cx } from '@/lib/cx'
import { fullscreenTileVideo } from '@/lib/fullscreen'
import { ScreenShareModal } from './ScreenShareModal'
import { StreamStats } from './StreamStats'
import { CameraFlip } from './CameraFlip'
import { TilePip } from './TilePip'
import { TrackVideo } from './TrackVideo'
import { TileFullscreenButton } from './TileFullscreenButton'
import type { User, VoiceState } from '@/types/api'
import { Avatar } from '../Avatar'
import { EffectMark } from './EffectMark'
import room from './VoiceRoom.module.css'
import styles from './CallPanel.module.css'
import { canShareScreen } from '@/lib/screenShare'

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`

/** Панель звонка над сообщениями ЛС: кто в звонке, экраны и камеры, свои кнопки; для тех, кто не в звонке — «Присоединиться». */
export function CallPanel({ channelId, users }: { channelId: string; users: Record<string, User> }) {
  const me = useSocialStore((s) => s.currentUserId)
  const call = useSocialStore((s) => s.calls[channelId])
  const states = useSocialStore((s) => s.callStates[channelId])
  const ringCall = useSocialStore((s) => s.ringCall)
  const connection = useVoiceStore((s) => s.connection)
  const selfMute = useVoiceStore((s) => s.selfMute)
  const selfDeaf = useVoiceStore((s) => s.selfDeaf)
  const sharing = useVoiceStore((s) => s.sharing)
  const camera = useVoiceStore((s) => s.camera)
  const speaking = useVoiceStore((s) => s.speaking)
  const screens = useVoiceStore((s) => s.screens)
  const cameras = useVoiceStore((s) => s.cameras)
  const error = useVoiceStore((s) => s.error)
  const micError = useVoiceStore((s) => s.micError)
  const { join, leave, toggleMute, toggleDeaf, toggleCamera, startScreen, stopScreen, retryMic } = useVoiceStore()
  const sharePrefs = useVoiceStore((s) => s.prefs.screenShare)
  const [shareModal, setShareModal] = useState(false)
  const [stats, setStats] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const connected = connection?.channelId === channelId
  const participants: VoiceState[] = Object.values(states ?? {})
  if (!call && !connected) return null
  const nameOf = (id: string) => {
    const u = users[id]
    return u ? displayName(u) : id
  }
  const avatarOf = (id: string) => avatarUrl(id, users[id]?.avatar)
  const decorationOf = (id: string) => users[id]?.avatar_decoration_data
  const ringing = (call?.ringing ?? []).filter((id) => id !== me)
  const notInCall = Object.keys(users).filter((id) => id !== me && !participants.some((p) => p.user_id === id) && !ringing.includes(id))

  const connect = async () => {
    setBusy(true)
    try {
      await join(null, channelId)
    } catch {
      /* ошибка в сторе */
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={styles.panel} aria-label="Звонок">
      <div className={styles.grid}>
        {participants.map((vs) => {
          const label = nameOf(vs.user_id)
          const screen = screens[vs.user_id]
          const cam = cameras[vs.user_id]
          const isMe = vs.user_id === me
          return (
            <div key={vs.user_id} className={cx(room.tile, styles.tile, (screen || cam) && styles.tileVideo, speaking[vs.user_id] && room.tileSpeaking, screen && room.tileScreen)} data-user-id={vs.user_id} onDoubleClick={(screen || cam) ? fullscreenTileVideo : undefined}>
              {screen ? <TrackVideo track={screen} className={room.video} /> : cam ? <TrackVideo track={cam} className={room.camera} /> : (
                <Avatar initial={label.slice(0, 1).toUpperCase()} color={colorOf(vs.user_id)} size={56} src={avatarOf(vs.user_id)} decoration={decorationOf(vs.user_id)} />
              )}
              <EffectMark userId={vs.user_id} size={26} className={room.tileEffect} />
              <div className={room.tileFooter}>
                <span className={room.tileName}>{label}{isMe ? ' (вы)' : ''}</span>
                <span className={room.tileIcons}>
                  {vs.self_stream ? <Monitor size={14} aria-label="Демонстрирует экран" /> : null}
                  {vs.self_mute ? <MicOff size={14} aria-label="Микрофон выключен" /> : null}
                  {vs.self_deaf ? <HeadphoneOff size={14} aria-label="Звук выключен" /> : null}
                </span>
              </div>
              {screen && stats === vs.user_id ? <StreamStats track={screen} own={isMe} /> : null}
              {screen ? (
                <button
                  type="button"
                  className={room.tileStats}
                  data-tooltip={stats === vs.user_id ? 'Скрыть цифры трансляции' : 'Разрешение, кадры и битрейт'}
                  aria-label="Цифры трансляции"
                  aria-pressed={stats === vs.user_id}
                  onClick={() => setStats(stats === vs.user_id ? null : vs.user_id)}
                >
                  <Activity size={16} />
                </button>
              ) : null}
              {(screen || cam) ? (
                <TileFullscreenButton userId={vs.user_id} className={room.tileFullscreen} />
              ) : null}
              {/* на плитке одно видео: экран, если человек его показывает, иначе камера */}
              {!isMe && screen ? <TilePip kind="screen" userId={vs.user_id} track={screen} name={label} /> : null}
              {!isMe && !screen && cam ? <TilePip kind="camera" userId={vs.user_id} track={cam} name={label} /> : null}
            </div>
          )
        })}
        {ringing.map((id) => (
          <div key={id} className={cx(room.tile, styles.tile, styles.tileRinging)}>
            <Avatar initial={nameOf(id).slice(0, 1).toUpperCase()} color={colorOf(id)} size={56} src={avatarOf(id)} decoration={decorationOf(id)} />
            <div className={room.tileFooter}>
              <span className={room.tileName}>{nameOf(id)}</span>
              <span className={styles.ringingLabel}>звоним…</span>
            </div>
          </div>
        ))}
      </div>
      {error || micError ? (
        <p className={room.error} role="alert">
          {error ?? micError}
          {micError ? <button type="button" className={room.retry} onClick={() => void retryMic()}>Попробовать снова</button> : null}
        </p>
      ) : null}
      <div className={styles.controls}>
        {connected ? (
          <>
            <span className={styles.status}>
              {connection?.status === 'connected' ? 'Звонок идёт' : connection?.status === 'reconnecting' ? 'Переподключение…' : 'Подключение…'}
              {' · '}{participants.length} {plural(participants.length, 'участник', 'участника', 'участников')}
            </span>
            {notInCall.length > 0 ? (
              <button type="button" className={styles.ring} onClick={() => void ringCall(channelId, notInCall)}>Позвонить ещё раз</button>
            ) : null}
            <button type="button" className={cx(room.control, styles.control, selfMute && room.controlOff)} data-tooltip={selfMute ? 'Включить микрофон' : 'Выключить микрофон'} aria-label={selfMute ? 'Включить микрофон' : 'Выключить микрофон'} aria-pressed={selfMute} onClick={() => void toggleMute()}>
              {selfMute ? <MicOff size={20} /> : <Mic size={20} />}
            </button>
            <button type="button" className={cx(room.control, styles.control, selfDeaf && room.controlOff)} data-tooltip={selfDeaf ? 'Включить звук' : 'Выключить звук'} aria-label={selfDeaf ? 'Включить звук' : 'Выключить звук'} aria-pressed={selfDeaf} onClick={() => void toggleDeaf()}>
              {selfDeaf ? <HeadphoneOff size={20} /> : <Headphones size={20} />}
            </button>
            <button type="button" className={cx(room.control, styles.control, camera && room.controlOn)} data-tooltip={camera ? 'Выключить камеру' : 'Включить камеру'} aria-label={camera ? 'Выключить камеру' : 'Включить камеру'} aria-pressed={camera} onClick={() => void toggleCamera()}>
              {camera ? <VideoOff size={20} /> : <Video size={20} />}
            </button>
            <CameraFlip className={cx(room.control, styles.control)} size={20} />
            {canShareScreen ? (
            <button type="button" className={cx(room.control, styles.control, sharing && room.controlOn)} data-tooltip={sharing ? 'Остановить демонстрацию' : 'Демонстрация экрана'} aria-label={sharing ? 'Остановить демонстрацию' : 'Демонстрация экрана'} aria-pressed={sharing} onClick={() => { if (sharing) void stopScreen(); else setShareModal(true) }}>
              {sharing ? <MonitorOff size={20} /> : <Monitor size={20} />}
            </button>
            ) : null}
            <button type="button" className={cx(room.control, styles.control, room.controlDanger)} data-tooltip="Завершить" aria-label="Завершить звонок" onClick={() => void leave()}>
              <PhoneOff size={20} />
            </button>
          </>
        ) : (
          <>
            <span className={styles.status}>Идёт звонок · {participants.length} {plural(participants.length, 'участник', 'участника', 'участников')}</span>
            <button type="button" className={room.connect} disabled={busy} onClick={() => void connect()}>
              {busy ? 'Подключаем…' : 'Присоединиться'}
            </button>
          </>
        )}
      </div>
      {shareModal ? <ScreenShareModal initial={sharePrefs} onStart={startScreen} onClose={() => setShareModal(false)} /> : null}
    </section>
  )
}

